import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { safeExecute } from '@/utils/function.util';
import { isEqual, PropertyPath } from '@/utils/lodash.util';
import { useGetComponentDidMount } from '@/utils/reactHooks.util';
import { appStorage, appStorageKey } from '@/utils/storage/appStorage.util';
import {
  getTenantScopedKey,
  isTenantScopedFilterKey,
  migrateLegacyKey,
  TENANT_CHANGE_EVENT,
} from '@/utils/storage/tenantScoping.util';

export enum StorageKey {
  LAST_VISITED_USER_ID = 'lastVisitedUserId',
  AUTH = 'chariot.auth',
  SHOW_NEW_USER_SEED_MODAL = 'chariot.showNewUserSeedModal',
  DRAWER_COMPOSITE_KEY = 'drawerCompositeKey',
  DRAWER_KEY = 'k',
  HASH_SEARCH = 'hashSearch',
  GENERIC_SEARCH = 'q',
  POE = 'POE',
  RUNNING_JOBS = 'RUNNING_JOBS',
  RESIZE = 'resizeColumn',
  RISK_JOB_MAP = 'riskJobMap',
  AWS_MARKETPLACE_CONFIG = 'awsMarketplaceConfig',
  AWS_MARKETPLACE_CONFIG_VERIFY_LINKING = 'awsMarketplaceConfigVerifyLinking',
  SCAN_SCHEDULE_TIMEZONE = 'scanScheduleTimezone',
  SORTABLE_COLUMNS = 'sortableColumn',
  FILTERS_SEEDS = 'filters.seeds',
  FILTERS_ASSETS = 'filters.assets.v1',
  FILTERS_RISKS = 'filters.risks',
  FILTERS_TECHNOLOGIES = 'filters.technologies',
  FILTERS_TECHNOLOGY_ASSETS = 'filters.technologyAssets',
  FILTERS_CUSTOMER_MANAGEMENT = 'filters.customerManagement.v1',
  ADVANCE_EXPORT_OPTIONS = 'advanceExportOptions',
}

/**
 * Resolve the effective storage key, applying tenant scoping if needed.
 *
 * For filter-related keys (FILTERS_*), this:
 * 1. Migrates any legacy unscoped data to the tenant-scoped key
 * 2. Returns the tenant-scoped key
 *
 * For non-filter keys, returns the original key unchanged.
 *
 * @param key - The storage key (string or PropertyPath)
 * @returns The effective storage key to use
 */
function resolveStorageKey(key: PropertyPath): PropertyPath {
  // Convert PropertyPath to string for comparison
  const keyString = Array.isArray(key) ? key.join('.') : key.toString();

  // Check if this key needs tenant scoping
  if (isTenantScopedFilterKey(keyString)) {
    // Attempt migration of legacy key (no-op if already migrated)
    migrateLegacyKey(keyString);

    // Return the tenant-scoped key
    return getTenantScopedKey(keyString);
  }

  // Non-filter keys are returned unchanged
  return key;
}

interface UseStorageOptions<S> {
  key?: PropertyPath;
  queryKey?: string;
  queryValidate?: (value: S) => boolean;
  parentState?: S;
  onParentStateChange?: Dispatch<SetStateAction<S>> | ((value: S) => void);
}

export function useStorage<S>(
  options: UseStorageOptions<S>,
  initialState: S | (() => S)
): [S, Dispatch<SetStateAction<S>>, () => void];

// eslint-disable-next-line no-redeclare
export function useStorage<S = undefined>(options: {
  key?: PropertyPath;
  queryKey?: string;
  queryValidate?: (value: S) => boolean;
  parentState?: S;
  onParentStateChange?:
    | Dispatch<SetStateAction<S | undefined>>
    | ((value: S | undefined) => void);
}): [S | undefined, Dispatch<SetStateAction<S | undefined>>, () => void];

// eslint-disable-next-line no-redeclare
export function useStorage<S>(
  options: {
    key?: PropertyPath;
    queryKey?: string;
    queryValidate?: (value: S) => boolean;
    parentState?: S;
    onParentStateChange?:
      | Dispatch<SetStateAction<S | undefined>>
      | ((value: S | undefined) => void);
  },
  initialState?: S | (() => S)
) {
  const {
    key: localStorageKey,
    queryKey,
    queryValidate,
    parentState,
    onParentStateChange,
  } = options;

  // Track tenant changes to trigger storage key re-computation
  // When tenant changes (impersonation start/stop), increment version to re-resolve storage key
  const [tenantVersion, setTenantVersion] = useState(0);

  // Listen for tenant change events (dispatched during impersonation)
  useEffect(() => {
    const handleTenantChange = () => {
      setTenantVersion(v => v + 1);
    };

    window.addEventListener(TENANT_CHANGE_EVENT, handleTenantChange);
    return () =>
      window.removeEventListener(TENANT_CHANGE_EVENT, handleTenantChange);
  }, []);

  // Resolve the effective storage key (applies tenant scoping for filter keys)
  // This is memoized to avoid re-computing on every render
  // Re-computes when tenant changes (via tenantVersion) to use new tenant's storage
  const effectiveStorageKey = useMemo(
    () => (localStorageKey ? resolveStorageKey(localStorageKey) : undefined),
    [localStorageKey, tenantVersion]
  );

  const queryValue = queryKey
    ? new URL(window.location.href).searchParams.get(queryKey)
    : '';

  const componentDidMount = useGetComponentDidMount();

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- Intentional: compute initial default value once on mount. Adding deps would break "initial value" semantics.
  const defaultValue = useMemo(() => {
    return parentState === undefined
      ? typeof initialState === 'function'
        ? (initialState as () => S)()
        : initialState
      : parentState;
  }, []);

  const [localValue, setLocalValue] = useState<S | undefined>(getDefaultValue);

  // Re-read from storage when effectiveStorageKey changes (tenant switch)
  // This ensures that when a user switches tenants (impersonation), the hook
  // reads the new tenant's stored data instead of keeping stale data.
  useEffect(() => {
    if (effectiveStorageKey) {
      const storedValue = appStorage.getItem<S>(effectiveStorageKey);
      const newValue = storedValue ?? defaultValue;

      // Only update if value actually changed (prevent unnecessary re-renders)
      if (!isEqual(newValue, localValue)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Valid: synchronizing localStorage (external system) with React state on tenant change
        setLocalValue(newValue);

        // Also notify parent if callback exists
        if (onParentStateChange && !isEqual(newValue, parentState)) {
          onParentStateChange(newValue);
        }
      }
    }
  }, [effectiveStorageKey]); // Trigger when storage key changes

  function updateStorage(value: S | undefined) {
    if (effectiveStorageKey || queryKey) {
      if (isEqual(value, defaultValue)) {
        if (effectiveStorageKey) {
          appStorage.removeItem(effectiveStorageKey);
        }
        if (queryKey) {
          const url = new URL(window.location.href);
          url.searchParams.delete(queryKey);
          window.history.replaceState({}, '', url.toString());
        }
      } else {
        if (effectiveStorageKey) {
          appStorage.setItem(effectiveStorageKey, value);
        }
        if (queryKey) {
          const url = new URL(window.location.href);
          url.searchParams.set(queryKey, JSON.stringify(value));
          window.history.replaceState({}, '', url.toString());
        }
      }
    }
  }

  useEffect(() => {
    if (componentDidMount) {
      const errorMessage =
        `useStorage: Key should not be changing after mount\n` +
        `  localStorageKey: ${JSON.stringify(localStorageKey)}\n` +
        `  queryKey: ${queryKey}\n` +
        `  parentState: ${JSON.stringify(parentState)}\n` +
        `  initialState: ${JSON.stringify(typeof initialState === 'function' ? 'function' : initialState)}`;

      // Only throw in production to prevent HMR errors in development
      if (import.meta.env.PROD) {
        const error = new Error(errorMessage);
        console.error(error);
        throw error;
      }
    }
  }, [localStorageKey, queryKey]);

  function getDefaultValue() {
    if (effectiveStorageKey || queryKey) {
      let localDefaultValue = defaultValue;

      if (effectiveStorageKey) {
        localDefaultValue =
          appStorage.getItem<S>(effectiveStorageKey) ?? localDefaultValue;
      }

      if (queryKey) {
        localDefaultValue = queryValue
          ? safeExecute<S>(
              () =>
                queryValidate
                  ? queryValidate(JSON.parse(queryValue))
                    ? JSON.parse(queryValue)
                    : localDefaultValue
                  : JSON.parse(queryValue),
              localDefaultValue
            )
          : localDefaultValue;
      }

      return localDefaultValue;
    }

    return defaultValue;
  }

  function setLocalValueFn(value: SetStateAction<S | undefined>) {
    if (typeof value === 'function') {
      setLocalValue(prevValue => {
        const updatedValue = (
          value as (prevState: S | undefined) => S | undefined
        )(prevValue);

        updateStorage(updatedValue);
        // ✅ Removed: onParentStateChange?.(updatedValue);
        // Parent will be notified via useEffect below
        return updatedValue;
      });
    } else {
      updateStorage(value);
      // ✅ Removed: onParentStateChange?.(value);
      // Parent will be notified via useEffect below
      setLocalValue(value);
    }
  }

  // REACT 19 COMPLIANCE FIX:
  // Moving onParentStateChange to useEffect ensures parent state updates
  // happen in a separate render cycle, preventing React's "Cannot update
  // component while rendering" warning. This is the proper pattern for
  // derived state synchronization in React 19+.
  //
  // INFINITE LOOP FIX:
  // Using JSON.stringify for deep equality comparison to prevent infinite loops
  // with array values. Reference comparison (localValue !== parentState) always
  // returns true for arrays even with identical contents, causing continuous
  // re-renders and flickering UI.
  useEffect(() => {
    if (!onParentStateChange) return;

    // Deep equality check for arrays and objects to prevent infinite loops
    const localValueStr = JSON.stringify(localValue);
    const parentStateStr = JSON.stringify(parentState);

    if (localValueStr !== parentStateStr) {
      onParentStateChange(localValue);
    }
    // Note: We deliberately don't include onParentStateChange in deps to avoid loops
  }, [localValue, parentState]);

  function resetValue() {
    updateStorage(defaultValue);
    // ✅ Removed: onParentStateChange?.(defaultValue);
    // Parent will be notified via useEffect above
    setLocalValue(defaultValue);
  }

  useEffect(() => {
    function reload(event: StorageEvent) {
      if (event.key === appStorageKey) {
        if (effectiveStorageKey) {
          setLocalValueFn(getDefaultValue);
        }
      }
    }

    window.addEventListener('storage', reload);
    return () => window.removeEventListener('storage', reload);
  }, [effectiveStorageKey]);

  useEffect(() => {
    if (queryKey) {
      const handlePopState = () => {
        const currentValue = getDefaultValue();
        if (!isEqual(currentValue, localValue)) {
          setLocalValue(currentValue);
        }
      };

      // Listen for browser back/forward button and manual URL changes
      window.addEventListener('popstate', handlePopState);

      // Also check on mount in case URL was changed externally
      const currentValue = getDefaultValue();
      if (!isEqual(currentValue, localValue)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Valid: synchronizing browser history (external system) with React state
        setLocalValue(currentValue);
      }

      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [queryKey]);

  useEffect(() => {
    if (parentState !== undefined) {
      if (!isEqual(parentState, localValue)) {
        updateStorage(parentState);
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Valid: controlled component pattern syncing parent state to local state
        setLocalValue(parentState);
      }
    }
  }, [parentState]);

  return [parentState ?? localValue, setLocalValueFn, resetValue];
}
